// @ftedn/shared — Feature flag definitions (typed registry)
// Single source of truth for all feature flags across the platform.
// Each mechanic exposes typed config values, not just on/off.

export type FlagType = 'bool' | 'int' | 'enum';

export interface BoolFlagDef {
  type: 'bool';
  default: boolean;
  description: string;
}

export interface IntFlagDef {
  type: 'int';
  default: number;
  min: number;
  max: number;
  description: string;
}

export interface EnumFlagDef {
  type: 'enum';
  default: string;
  options: readonly string[];
  description: string;
}

export type FlagDef = BoolFlagDef | IntFlagDef | EnumFlagDef;

/**
 * Master flag registry.
 * Resolution order: env defaults → cohort → user → per-mechanic config.
 * Highest specificity wins.
 */
export const FLAG_DEFINITIONS = {
  // ── Streaks ──────────────────────────────────────────
  'mechanic.streaks.enabled': {
    type: 'bool',
    default: true,
    description: 'Enable daily streak tracking and UI surfaces',
  },
  'mechanic.streaks.graceDays': {
    type: 'int',
    default: 1,
    min: 0,
    max: 7,
    description: 'Number of grace days per week before streak resets',
  },

  // ── Quests ───────────────────────────────────────────
  'mechanic.quests.enabled': {
    type: 'bool',
    default: true,
    description: 'Enable quest board and daily/weekly challenges',
  },
  'mechanic.quests.dailyCount': {
    type: 'int',
    default: 3,
    min: 1,
    max: 10,
    description: 'Number of daily quests available to each user',
  },

  // ── Leaderboard ──────────────────────────────────────
  'mechanic.leaderboard.enabled': {
    type: 'bool',
    default: true,
    description: 'Enable leaderboard surfaces',
  },
  'mechanic.leaderboard.scope': {
    type: 'enum',
    default: 'global',
    options: ['global', 'cohort', 'friends'] as const,
    description: 'Default leaderboard scope for users',
  },

  // ── Referrals ────────────────────────────────────────
  'mechanic.referrals.enabled': {
    type: 'bool',
    default: true,
    description: 'Enable referral link generation and claiming',
  },
  'mechanic.referrals.bonusXp': {
    type: 'int',
    default: 100,
    min: 0,
    max: 1000,
    description: 'XP bonus awarded to both referrer and referee on first claim',
  },

  // ── Open Co-creation ─────────────────────────────────
  'mechanic.openCocreation.enabled': {
    type: 'bool',
    default: true,
    description: 'Show public timelines, live vote tallies, transparent phase callouts',
  },

  // ── Visible Impact ───────────────────────────────────
  'mechanic.visibleImpact.enabled': {
    type: 'bool',
    default: true,
    description: 'Show "your fingerprint" surfaces — vote impact, ingredient credits',
  },

  // ── Circuit Breakers (I1/I2 cost caps) ──────────────
  'circuit.sms.enabled': {
    type: 'bool',
    default: true,
    description: 'Master SMS circuit breaker — set false to halt all outbound SMS',
  },
  'circuit.email.enabled': {
    type: 'bool',
    default: true,
    description: 'Master email circuit breaker — set false to halt all outbound email',
  },
  'circuit.push.enabled': {
    type: 'bool',
    default: true,
    description: 'Master push circuit breaker — set false to halt outbound push notifications',
  },

  // ── Push Notifications (I4) ─────────────────────────
  'mechanic.push.promptEnabled': {
    type: 'bool',
    default: true,
    description: 'Show push notification permission prompt after delight moment',
  },
  'mechanic.push.promptCooldownDays': {
    type: 'int',
    default: 7,
    min: 1,
    max: 90,
    description: 'Days to wait before re-prompting after user dismisses push permission',
  },
} as const;

export type FlagName = keyof typeof FLAG_DEFINITIONS;
export type FlagValue<N extends FlagName> =
  typeof FLAG_DEFINITIONS[N] extends BoolFlagDef ? boolean :
  typeof FLAG_DEFINITIONS[N] extends IntFlagDef ? number :
  typeof FLAG_DEFINITIONS[N] extends EnumFlagDef ? string :
  never;

/** Group flags by mechanic for admin UI sections */
export function getFlagsByMechanic(): Record<string, { name: FlagName; def: FlagDef }[]> {
  const groups: Record<string, { name: FlagName; def: FlagDef }[]> = {};

  for (const [name, def] of Object.entries(FLAG_DEFINITIONS)) {
    const parts = name.split('.');
    const mechanic = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : parts[0];
    if (!groups[mechanic]) groups[mechanic] = [];
    groups[mechanic].push({ name: name as FlagName, def: def as FlagDef });
  }

  return groups;
}
